import { VOICE_READ_EXACT_MAX_CHARS } from "@/config/assistant-identity";
import type { AssistantSettingsDto } from "@/types/ai-assistant-admin";
import {
	VOICE_NAME_PATTERNS,
	VOICE_SETTINGS_LIMITS,
	WALL_NOC_MIC_MODES,
	WALL_VOICE_MODES,
} from "@/types/ai-assistant-voice";

/**
 * Validasi setelan suara (Fitur 3) — dipakai form admin dan server supaya
 * aturannya sama. Angka: rentang `VOICE_SETTINGS_LIMITS`; teks: pola nama
 * model/suara GPT-Live dan batas instruksi baca-persis.
 */

type VoiceTextKey =
	| "voiceLiveModel"
	| "voiceName"
	| "voiceReadExactInstruction"
	| "wallVoiceMode"
	| "wallNocMicMode";
export type VoiceNumberKey =
	| "voiceDailyMinutesUser"
	| "voiceDailyMinutesKiosk"
	| "voiceSessionMaxMinutes"
	| "voiceIdleOffSeconds"
	| "wallNocVolume"
	| "wallNocSettleMs";
export type VoiceSettingsKey = VoiceTextKey | VoiceNumberKey;

const L = VOICE_SETTINGS_LIMITS;

/** Rentang angka suara. `zeroUnlimited` = 0 berarti tanpa batas. */
export const VOICE_LIMIT_RULES: Record<
	VoiceNumberKey,
	{
		label: string;
		/** Keterangan tambahan di bawah input (setelah "0 = tanpa batas"). */
		hint?: string;
		min: number;
		max: number;
		zeroUnlimited: boolean;
	}
> = {
	voiceDailyMinutesUser: {
		label: "Menit suara per hari per user",
		hint: "Plafon semua user ada di kartu Plafon AI di bawah",
		min: 0,
		max: L.dailyMinutesMax,
		zeroUnlimited: true,
	},
	voiceDailyMinutesKiosk: {
		label: "Menit suara per hari akun kiosk",
		min: 0,
		max: L.dailyMinutesMax,
		zeroUnlimited: true,
	},
	voiceSessionMaxMinutes: {
		label: "Batas satu sesi suara (menit)",
		min: L.sessionMaxMinutesMin,
		max: L.sessionMaxMinutesMax,
		zeroUnlimited: false,
	},
	voiceIdleOffSeconds: {
		label: "Matikan otomatis bila hening (detik)",
		min: L.idleOffSecondsMin,
		max: L.idleOffSecondsMax,
		zeroUnlimited: false,
	},
	wallNocVolume: {
		label: "Volume suara Jenna di Mode NOC (%)",
		min: L.nocVolumeMin,
		max: L.nocVolumeMax,
		zeroUnlimited: false,
	},
	wallNocSettleMs: {
		label: "Ekor hening sebelum mic dibuka lagi (ms)",
		hint: "Hanya untuk perilaku mic otomatis",
		min: L.nocSettleMsMin,
		max: L.nocSettleMsMax,
		zeroUnlimited: false,
	},
};

/** Error per field suara; objek kosong = valid. */
export function validateVoiceSettings(
	s: Pick<AssistantSettingsDto, VoiceSettingsKey>,
): Partial<Record<VoiceSettingsKey, string>> {
	const errors: Partial<Record<VoiceSettingsKey, string>> = {};
	if (!VOICE_NAME_PATTERNS.model.test(s.voiceLiveModel.trim()))
		errors.voiceLiveModel =
			"Nama model tidak valid (huruf, angka, titik, - atau _)";
	const voice = s.voiceName?.trim() ?? "";
	if (voice && !VOICE_NAME_PATTERNS.voice.test(voice))
		errors.voiceName = "Nama suara tidak valid (huruf kecil, angka, - atau _)";
	if (
		(s.voiceReadExactInstruction?.trim().length ?? 0) >
		VOICE_READ_EXACT_MAX_CHARS
	)
		errors.voiceReadExactInstruction = `Maksimal ${VOICE_READ_EXACT_MAX_CHARS} karakter`;
	if (!(WALL_VOICE_MODES as readonly string[]).includes(s.wallVoiceMode))
		errors.wallVoiceMode = "Mode suara di wall tidak dikenal";
	if (!(WALL_NOC_MIC_MODES as readonly string[]).includes(s.wallNocMicMode))
		errors.wallNocMicMode = "Perilaku mikrofon Mode NOC tidak dikenal";
	for (const key of Object.keys(VOICE_LIMIT_RULES) as VoiceNumberKey[]) {
		const { min, max } = VOICE_LIMIT_RULES[key];
		const v = s[key];
		if (!Number.isInteger(v) || v < min || v > max)
			errors[key] =
				`Harus bilangan bulat ${min}–${max.toLocaleString("id-ID")}`;
	}
	return errors;
}
